import type { PresentationMode } from "@/hooks/usePresentationView";

export interface InsertTarget {
  /** The unit index to insert into. */
  index: number;
  /** The unit was not locked — lock it first (a GRAB), and do not scroll. */
  needsLock: boolean;
}

/**
 * Which Zwischenabschnitt an `Enter` / `A` insert goes into, while presenting.
 *
 * 🔴 In `free` mode it GRABS the unit at the reading line and inserts there — it does
 * NOT stand down.
 *
 * The bug this fixes: the insert used to require `mode === "locked"`, but
 *   - a presentation STARTS in `free` (usePresentationView.enter), and
 *   - every wheel scroll calls `release()` → back to `free`,
 * while `handleKey` returns false for Enter, so Enter can never lock a unit itself.
 * Enter was therefore a SILENT no-op immediately after you start presenting, and again
 * after every scroll — with the hint bar still advertising "Enter Eintrag". It only
 * appeared to work once the writer had pressed an arrow key first, which is why it read
 * as "works on the third or fourth try".
 *
 * Grabbing is the semantically right answer, not a workaround: the grab exists precisely
 * to say *which section the reader is looking at*, and "insert into the section I am
 * looking at" is what the key means.
 */
export function resolveInsertTarget(args: {
  mode: PresentationMode;
  focusedUnit: number | null;
  /** The unit at the reading line — see pickNearestUnit. */
  nearestUnit: number;
  unitCount: number;
}): InsertTarget | null {
  const { mode, focusedUnit, nearestUnit, unitCount } = args;
  if (unitCount <= 0) return null;

  const locked = mode === "locked" && focusedUnit !== null;
  const index = locked ? focusedUnit : nearestUnit;
  if (index < 0 || index >= unitCount) return null;

  return { index, needsLock: !locked };
}
